import { Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, Checkbox, ListItemText, CircularProgress, Box, Typography, Divider } from "@mui/material";
import { FC, Fragment, useCallback, useState, useMemo, useEffect } from "react";
import { OrderLineItemDto } from "../../../dtos";
import { useUpdateDispatchUpdateTimesMutation } from "../../../store/generated/generatedApi";
import { AffectedDispatchDto } from "../../../dtos/generated/AffectedDispatchDto";
import { formatDate } from "../../../util";

interface Props {
    open: boolean;
    orderLineItems?: OrderLineItemDto[];
    affectedDispatches?: AffectedDispatchDto[];
    isLoading?: boolean;
    onSuccess: () => void;
    onCancel: () => void;
}

export const AffectedDispatchesDialog: FC<Props> = ({
    open,
    orderLineItems = [],
    affectedDispatches = [],
    isLoading = false,
    onSuccess,
    onCancel
}) => {
    const [updateDispatchTimes, { isLoading: isUpdating, reset: resetUpdateDispatchTimes }] = useUpdateDispatchUpdateTimesMutation();

    const [selectedDispatchIds, setSelectedDispatchIds] = useState<string[]>([]);

    const groupedByOrderLineItem = useMemo(() => {
        const result: Record<string, AffectedDispatchDto[]> = {};
        if (affectedDispatches && orderLineItems.length > 0) {
            orderLineItems.forEach((oli) => {
                result[oli.id] = affectedDispatches.filter((d) => d.dispatch.orderLineItemId === oli.id);
            });
        }
        return result;
    }, [affectedDispatches, orderLineItems]);

    const handleCancel = useCallback(() => {
        resetUpdateDispatchTimes();
        onCancel();
    }, [resetUpdateDispatchTimes, onCancel]);

    const handleSuccess = useCallback(() => {
        resetUpdateDispatchTimes();
        onSuccess();
    }, [resetUpdateDispatchTimes, onSuccess]);

    const handleToggle = useCallback((id: string) => {
        setSelectedDispatchIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    }, []);

    const handleConfirm = useCallback(async () => {
        if (!affectedDispatches) return;
        const selected = affectedDispatches.filter(d => selectedDispatchIds.includes(d.dispatch.id));
        if (selected.length === 0) {
            handleSuccess();
            return;
        }
        try {
            await updateDispatchTimes(selected).unwrap();
            handleSuccess();
        } catch (e) {
            console.error("Failed to update dispatches:", e);
        }
    }, [affectedDispatches, selectedDispatchIds, updateDispatchTimes, handleSuccess]);

    useEffect(() => {
        if (affectedDispatches && affectedDispatches.length > 0) {
            setSelectedDispatchIds(affectedDispatches.map((d) => d.dispatch.id));
        }
    }, [affectedDispatches]);

    const isOpen = useMemo(() => {
        return open && affectedDispatches && affectedDispatches.length > 0;
    }, [open, affectedDispatches]);

    return (
        <Dialog open={isOpen} onClose={handleCancel} maxWidth="sm" fullWidth>
            <DialogTitle>Affected Dispatches</DialogTitle>
            <DialogContent>
                {isLoading || isUpdating ? (
                    <CircularProgress />
                ) : (
                    <Box mt={2}>
                        <List
                            sx={{
                                maxHeight: 500,
                                overflowY: 'scroll',
                                border: '1px solid lightgray',
                                borderRadius: 2,
                                bgcolor: 'background.paper'
                            }}
                        >
                            {orderLineItems.length === 0 || !affectedDispatches || affectedDispatches.length === 0 ? (
                                <ListItem>
                                    <ListItemText primary="No affected dispatches." />
                                </ListItem>
                            ) : (orderLineItems
                                .filter(oli => groupedByOrderLineItem[oli.id] && groupedByOrderLineItem[oli.id].length > 0)
                                .map((oli, idx, filteredOrderLineItems) => (
                                    <Fragment key={oli.id}>
                                        <ListItem>
                                            <Box>
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    {`${oli.orderLineItemNumber} ${oli.name}`}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    Yard Time: {oli.yardTime ? formatDate(oli.yardTime, 'hh:mm aa') : "N/A"}
                                                    {" | "}
                                                    Onsite Time: {oli.onsiteTime ? formatDate(oli.onsiteTime, 'hh:mm aa') : "N/A"}
                                                </Typography>
                                            </Box>
                                        </ListItem>
                                        <Divider />
                                        {groupedByOrderLineItem[oli.id].map((affected) => (
                                            <ListItem
                                                key={affected.dispatch.id}
                                                sx={{ pl: 4 }}
                                                button
                                                onClick={() => handleToggle(affected.dispatch.id)}
                                            >
                                                <Checkbox
                                                    checked={selectedDispatchIds.includes(affected.dispatch.id)}
                                                />
                                                <Box>
                                                    <Typography variant="body1">
                                                        {affected.dispatch.broker?.name ?? ""}
                                                        {affected.dispatch.equipment
                                                            ? ` (${affected.dispatch.equipment.equipmentType?.type ?? ""} #${affected.dispatch.equipment.number ?? ""}) `
                                                            : ""}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Old:{" "}
                                                        {formatDate(affected.dispatch.startDate, 'hh:mm aa')}
                                                        {" "}-{" "}
                                                        {formatDate(affected.dispatch.endDate, 'hh:mm aa')}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        New:{" "}
                                                        {formatDate(affected.newStartTime, 'hh:mm aa')}
                                                        {" "}-{" "}
                                                        {formatDate(affected.newEndTime, 'hh:mm aa')}
                                                    </Typography>
                                                </Box>
                                            </ListItem>
                                        ))}
                                        {idx < filteredOrderLineItems.length - 1 && <Divider />}
                                    </Fragment>
                                ))
                            )}
                        </List>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel}>Update None</Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                >
                    Update Selected
                </Button>
            </DialogActions>
        </Dialog>
    );
};